<template>
    <v-form ref="form" @submit.prevent="$emit('submit')" :disabled="viewOnly">
        <form-section>
            <v-row>
                <v-col cols="12">
                    <div>What type of account do you need?</div>
                    <v-btn-toggle
                        v-model="model.organisation_type"
                        color="primary"
                    >
                        <v-btn :disabled="viewOnly" small value="limited">
                            Limited Company/PLC
                        </v-btn>
                        <v-btn :disabled="viewOnly" small value="sole_trader">
                            Sole Trader
                        </v-btn>
                    </v-btn-toggle>
                </v-col>
            </v-row>
        </form-section>

        <!--Company Info-->
        <form-section title="Company Details">
            <v-row>
                <v-col cols="12">
                    <text-field
                        :label="isLimitedCompany ? 'Company Name' : 'Your Name'"
                        :rules="[
                            requiredRule(
                                isLimitedCompany ? 'Company Name' : 'Your Name'
                            ),
                        ]"
                        required
                        v-model="model.company_name"
                    />
                </v-col>
            </v-row>

            <address-form
                :label="
                    isSoleTrader ? 'Home Address' : 'Office/Trading Address'
                "
                v-model="model.address"
            />

            <v-row>
                <v-col v-if="isSoleTrader" cols="12" md="6">
                    <text-field
                        label="Time at Home Address"
                        :rules="[requiredRule('Time at home address')]"
                        required
                        v-model="model.time_at_address"
                        hint="e.g. 6 years 2 months..."
                    />
                </v-col>

                <v-col v-if="isSoleTrader" cols="12" md="6">
                    <text-field
                        label="Date of Birth"
                        :rules="[requiredRule('Date of Birth')]"
                        required
                        v-model="model.dob"
                        type="date"
                        show-picker
                    />
                </v-col>

                <v-col v-if="isLimitedCompany" cols="12" md="6">
                    <text-field
                        label="Company Registration Number"
                        required
                        :rules="[requiredRule('Company Registration Number')]"
                        v-model="model.company_number"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field label="VAT Number" v-model="model.vat_number" />
                </v-col>

                <v-col v-if="isLimitedCompany" cols="12" md="6">
                    <text-field
                        label="Main Contact Name"
                        required
                        :rules="[requiredRule('Main Contact Name')]"
                        v-model="model.name"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        label="Email Address"
                        required
                        :rules="[requiredRule('Email')]"
                        v-model="model.email"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        :label="isSoleTrader ? 'Home Phone' : 'Office Phone'"
                        v-model="model.phone"
                    />
                </v-col>

                <v-col v-if="isSoleTrader" cols="12" md="6">
                    <text-field
                        label="Mobile Phone"
                        v-model="model.mobile_phone"
                    />
                </v-col>
            </v-row>
        </form-section>

        <!--Accounts Contact-->
        <form-section
            v-if="isLimitedCompany"
            title="Accounts Department Details"
        >
            <v-row>
                <v-col cols="12">
                    <text-field
                        label="Contact Name"
                        required
                        :rules="[requiredRule('Contact Name')]"
                        v-model="model.accounts_contact"
                    />
                </v-col>
            </v-row>

            <address-form
                label="Accounts Address"
                v-model="model.accounts_address"
                :hide-fields="model.accounts_address_same"
            >
                <template v-slot:top>
                    <v-checkbox
                        label="Same as Company Address"
                        v-model="model.accounts_address_same"
                    />
                    <div v-if="model.accounts_address_same">
                        <div v-for="(line, name) in model.address">
                            <div
                                v-if="name !== 'country_id'"
                                v-text="line"
                                class="text-pre-wrap"
                            />
                        </div>
                    </div>
                </template>
            </address-form>

            <v-row>
                <v-col cols="12" md="6">
                    <text-field
                        label="Accounts Phone"
                        v-model="model.accounts_phone"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        label="Accounts Email"
                        required
                        :rules="[requiredRule('Accounts Email')]"
                        v-model="model.accounts_email"
                    />
                </v-col>
            </v-row>
        </form-section>

        <!--Credit Facility-->
        <form-section title="Credit Account">
            <v-row>
                <v-col cols="12">
                    <div>Do you require a credit Facility?</div>

                    <v-btn-toggle
                        color="primary"
                        v-model="model.credit_required"
                    >
                        <v-btn :disabled="viewOnly" :value="false"> No</v-btn>
                        <v-btn :disabled="viewOnly" :value="true"> Yes</v-btn>
                    </v-btn-toggle>
                </v-col>

                <v-col v-if="model.credit_required" cols="12" md="6">
                    <text-field
                        label="Anticipated Monthly Credit Amount"
                        required
                        :rules="[requiredRule('Credit Limit')]"
                        v-model="model.credit_amount"
                        type="number"
                        prepend-icon="mdi-currency-gbp"
                    />
                </v-col>
            </v-row>
        </form-section>

        <!--Bank Details-->
        <form-section v-if="model.credit_required" title="Bank Details">
            <v-row>
                <v-col cols="12">
                    <text-field
                        label="Name of Banker"
                        required
                        :rules="[requiredRule('Banker Name')]"
                        v-model="model.banker_name"
                    />
                </v-col>
            </v-row>

            <address-form label="Bank Address" v-model="model.bank_address" />

            <v-row>
                <v-col cols="12">
                    <text-field
                        label="Bank Phone Number"
                        required
                        :rules="[requiredRule('Bank Phone')]"
                        v-model="model.bank_phone"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        label="Sort Code"
                        required
                        :rules="[requiredRule('Sort Code')]"
                        v-model="model.bank_sort_code"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        label="Account Number"
                        required
                        :rules="[requiredRule('Account Number')]"
                        v-model="model.bank_account_number"
                    />
                </v-col>
            </v-row>
        </form-section>

        <!--Trade References-->
        <form-section
            v-if="model.credit_required"
            title="First Trade Reference"
        >
            <v-row>
                <v-col cols="12">
                    <text-field
                        label="Company Name"
                        required
                        :rules="[requiredRule('Company Name')]"
                        v-model="model.ref_one_company_name"
                    />
                </v-col>
            </v-row>

            <address-form label="Address" v-model="model.ref_one_address" />

            <v-row>
                <v-col cols="12" md="6">
                    <text-field
                        label="Contact Name"
                        required
                        :rules="[requiredRule('Contact Name')]"
                        v-model="model.ref_one_contact_name"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <text-field
                        label="Email"
                        required
                        :rules="[requiredRule('Email')]"
                        v-model="model.ref_one_email"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        label="Phone"
                        required
                        :rules="[requiredRule('Phone')]"
                        v-model="model.ref_one_phone"
                    />
                </v-col>
            </v-row>
        </form-section>
        <form-section
            v-if="model.credit_required"
            title="Second Trade Reference"
        >
            <v-row>
                <v-col cols="12">
                    <text-field
                        label="Company Name"
                        required
                        :rules="[requiredRule('Company Name')]"
                        v-model="model.ref_two_company_name"
                    />
                </v-col>
            </v-row>

            <address-form label="Address" v-model="model.ref_two_address" />

            <v-row>
                <v-col cols="12" md="6">
                    <text-field
                        label="Contact Name"
                        required
                        :rules="[requiredRule('Contact Name')]"
                        v-model="model.ref_two_contact_name"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <text-field
                        label="Email"
                        required
                        :rules="[requiredRule('Email')]"
                        v-model="model.ref_two_email"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field
                        label="Phone"
                        required
                        :rules="[requiredRule('Phone')]"
                        v-model="model.ref_two_phone"
                    />
                </v-col>
            </v-row>
        </form-section>

        <form-section title="Identity Documents">
            <v-row>
                <v-col cols="12">
                    New customers need to provide one form of photo ID (a
                    driving licence or passport) and one proof of address (such
                    as a utility or council tax bill). Both documents should be
                    in PDF, JPEG or PNG format and uploaded below. This will
                    make the verification process easy and safe so you are able
                    to access our products and services faster.
                </v-col>

                <v-col cols="12" md="6" lg="4">
                    <v-sheet elevation="2" class="pa-2 fill-height">
                        <v-input
                            :rules="[requiredRule('Photo ID')]"
                            v-model="model.photo_id"
                        >
                            <div style="width: 100%">
                                <div class="text--secondary mb-1">
                                    Photo ID (required)
                                </div>

                                <file-uploader
                                    v-if="!viewOnly"
                                    v-model="model.photo_id"
                                    :allowed-extensions="allowedExtensions"
                                    dont-append-data-url-to-content-path
                                    @clear="
                                        () => {
                                            model.photo_id = null;
                                        }
                                    "
                                />
                            </div>
                        </v-input>
                        <v-btn
                            v-if="viewOnly"
                            target="_blank"
                            :href="model.photo_id.path"
                        >
                            View
                        </v-btn>
                    </v-sheet>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                    <v-sheet elevation="2" class="pa-2 fill-height">
                        <v-input
                            :rules="[requiredRule('Proof of Address')]"
                            v-model="model.proof_of_address"
                        >
                            <div style="width: 100%">
                                <div class="text--secondary mb-1">
                                    Proof of Address (required)
                                </div>
                                <file-uploader
                                    v-if="!viewOnly"
                                    v-model="model.proof_of_address"
                                    :allowed-extensions="allowedExtensions"
                                    dont-append-data-url-to-content-path
                                    @clear="
                                        () => {
                                            model.proof_of_address = null;
                                        }
                                    "
                                />
                            </div>
                        </v-input>
                        <v-btn
                            v-if="viewOnly"
                            target="_blank"
                            :href="model.proof_of_address.path"
                        >
                            View
                        </v-btn>
                    </v-sheet>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                    <v-sheet elevation="2" class="pa-2 fill-height">
                        <v-input
                            :rules="[
                                requiredRule('Proof of Hired In Insurance'),
                            ]"
                            v-model="model.insurance_proof"
                        >
                            <div style="width: 100%">
                                <div class="text--secondary mb-1">
                                    Hired In Equipment Insurance (required)
                                </div>
                                <file-uploader
                                    v-if="!viewOnly"
                                    v-model="model.insurance_proof"
                                    :allowed-extensions="allowedExtensions"
                                    dont-append-data-url-to-content-path
                                    @clear="
                                        () => {
                                            model.insurance_proof = null;
                                        }
                                    "
                                />
                            </div>
                        </v-input>
                        <v-btn
                            v-if="viewOnly"
                            target="_blank"
                            :href="model.insurance_proof.path"
                        >
                            View
                        </v-btn>
                    </v-sheet>
                </v-col>
            </v-row>
        </form-section>

        <!--Terms-->
        <form-section title="Communication Preferences">
            <v-row>
                <v-col cols="12">
                    Here at King Sound Reinforcement Ltd we take your privacy
                    seriously and will only use your personal information to
                    administer your account and to provide the products and
                    services you have requested from us. We’ll always treat your
                    personal details with the upmost care and will never sell
                    them to other companies for marketing purposes. We’d like
                    however from time to time to contact you with details of
                    products, offers and services we provide. If you consent to
                    us contacting you for this purpose please tick to say how
                    you would like us to contact you:
                </v-col>
                <v-col cols="12">
                    <v-checkbox
                        class="mt-0"
                        v-model="model.marketing_email"
                        label="Email"
                    />
                    <v-checkbox
                        class="mt-0"
                        v-model="model.marketing_post"
                        label="Post"
                    />
                    <v-checkbox
                        class="mt-0"
                        v-model="model.marketing_phone"
                        label="Telephone"
                    />
                    <v-checkbox
                        class="mt-0"
                        v-model="model.marketing_sms"
                        label="Text Message"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-checkbox
                        v-model="model.terms_accepted"
                        :rules="[
                            requiredRule('Terms', 'You must accept the Terms'),
                        ]"
                    >
                        <template v-slot:label>
                            I have read and agree to King Sound
                            Reinforcement's&nbsp;
                            <a
                                target="_blank"
                                :href="$page.props.statics.terms"
                                @click.stop
                            >
                                Terms and Conditions
                            </a>
                            , and&nbsp;
                            <a
                                target="_blank"
                                :href="$page.props.company_info.privacy_policy"
                                @click.stop
                            >
                                Privacy Policy
                            </a>
                        </template>
                    </v-checkbox>
                </v-col>
            </v-row>

            <v-row justify="center">
                <v-col cols="12" md="8">
                    <signature-field
                        v-if="!viewOnly"
                        v-model="model.signature_image"
                        ref="signature"
                        :hint="sigHint"
                    />
                    <v-img v-if="viewOnly" :src="model.signature_image.path" />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6">
                    <text-field
                        label="Full Name"
                        required
                        :rules="[requiredRule('Full Name')]"
                        v-model="model.signature_name"
                    />
                </v-col>

                <v-col v-if="isLimitedCompany" cols="12" md="6">
                    <text-field
                        label="Position"
                        required
                        v-model="model.signature_position"
                        :rules="[requiredRule('Position')]"
                    />
                </v-col>

                <v-col cols="12" md="6">
                    <text-field label="Date" :value="model.signature_date" />
                </v-col>

                <v-col cols="12" class="text-right" v-if="!viewOnly">
                    <v-btn
                        :loading="model.processing"
                        type="submit"
                        color="secondary"
                    >
                        Submit
                    </v-btn>
                </v-col>
            </v-row>
        </form-section>
    </v-form>
</template>

<script>
import VModel from "../../Mixins/VModel";
import FormSection from "../Forms/FormSection";
import AddressForm from "../Forms/AddressForm";
import TextField from "../Forms/TextField";
import SignatureField from "../Forms/SignatureField";
import ValidationRules from "../../Mixins/ValidationRules";
import FileUploader from "../Forms/FileUploader";

export default {
    name: "AccountFormFields",

    components: {
        FileUploader,
        SignatureField,
        TextField,
        AddressForm,
        FormSection,
    },

    mixins: [VModel, ValidationRules],

    props: {
        viewOnly: {
            default: false,
            type: Boolean,
        },
    },

    computed: {
        isLimitedCompany() {
            return this.model.organisation_type === "limited";
        },

        isSoleTrader() {
            return this.model.organisation_type === "sole_trader";
        },

        allowedExtensions() {
            return [".jpg", ".jpeg", ".png", ".pdf"];
        },

        sigHint() {
            return `By signing, you confirm you have read and agree to King Sound Reinforcement's&nbsp;<a
                                target="_blank"
                                :href="$page.props.statics.terms"
                                @click.stop
                            >Terms and Conditions</a>, and&nbsp;
                            <a
                                target="_blank"
                                :href="$page.props.company_info.privacy_policy"
                                @click.stop
                            >Privacy Policy</a>`;
        },
    },

    methods: {
        getSignature() {
            return this.$refs.signature.getBlob();
        },

        validate() {
            return this.$refs.form.validate();
        },
    },
};
</script>

<style scoped></style>
